import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Icon } from "../components/Icon.js";
import { coloringApi } from "./api.js";
import { useColoring } from "./ColoringContext.js";
import { CoverSheet } from "./CoverSheet.js";
import type { Leaf, PreviewPlan } from "./types.js";

type Sheet =
  | { key: string; kind: "cover-front" }
  | { key: string; kind: "cover-back" }
  | { key: string; kind: "leaf"; leaf: Leaf; leafNumber: number }
  | { key: string; kind: "paper" };

const PAPER: Sheet = { key: "paper", kind: "paper" };

/** A real-book flip-through of exactly what the print PDF will contain. */
export function PreviewPanel() {
  const { projectId, state, goTo } = useColoring();
  const { book, pages } = state;
  const [plan, setPlan] = useState<PreviewPlan | null>(null);
  const [view, setView] = useState(0);
  const [dir, setDir] = useState<"next" | "prev">("next");
  const [showBlanks, setShowBlanks] = useState(true);
  const [wide, setWide] = useState(true);
  const stageRef = useRef<HTMLDivElement | null>(null);

  const signature = pages.map((p) => `${p.id}:${p.currentVariantId ?? ""}`).join("|") + JSON.stringify(book.print) + (book.cover.frontVariantId ?? "") + (book.cover.backVariantId ?? "");
  useEffect(() => {
    let alive = true;
    coloringApi.previewPlan(projectId).then((p) => alive && setPlan(p)).catch(() => alive && setPlan(null));
    return () => {
      alive = false;
    };
  }, [projectId, signature]);

  // Two pages side by side when there is room, one page on narrow screens.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => setWide(el.clientWidth >= 720);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [plan]);

  const includeCover = book.print.includeCover;

  const sheets: Sheet[] = useMemo(() => {
    if (!plan) return [];
    const leaves = plan.leaves
      .map((leaf, i) => ({ leaf, n: i + 1 }))
      .filter((x) => showBlanks || x.leaf.kind !== "blank");
    return [
      ...(includeCover ? [{ key: "front", kind: "cover-front" } as Sheet] : []),
      ...leaves.map((x) => ({ key: `leaf-${x.n}`, kind: "leaf", leaf: x.leaf, leafNumber: x.n }) as Sheet),
      ...(includeCover ? [{ key: "back", kind: "cover-back" } as Sheet] : [])
    ];
  }, [plan, showBlanks, includeCover]);

  /** Each view is [left, right]; a null side shows the table. */
  const views: [Sheet | null, Sheet | null][] = useMemo(() => {
    if (!wide) return sheets.map((s) => [null, s] as [Sheet | null, Sheet | null]);
    const out: [Sheet | null, Sheet | null][] = [];
    let rest = sheets;
    if (includeCover && rest[0]?.kind === "cover-front") {
      out.push([null, rest[0]]);
      rest = rest.slice(1);
    }
    const back = includeCover && rest[rest.length - 1]?.kind === "cover-back" ? rest[rest.length - 1]! : null;
    if (back) rest = rest.slice(0, -1);
    // The first interior leaf is a right-hand page; its left neighbour is the inside cover.
    const interior = showBlanks ? [PAPER, ...rest] : rest;
    for (let i = 0; i < interior.length; i += 2) out.push([interior[i] ?? null, interior[i + 1] ?? (showBlanks ? PAPER : null)]);
    if (back) out.push([back, null]);
    return out;
  }, [sheets, wide, includeCover, showBlanks]);

  useEffect(() => setView((v) => Math.min(v, Math.max(0, views.length - 1))), [views.length]);

  const go = (delta: number) => {
    setDir(delta > 0 ? "next" : "prev");
    setView((v) => Math.max(0, Math.min(views.length - 1, v + delta)));
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT")) return;
      if (e.key === "ArrowRight" || e.key === "PageDown") go(1);
      else if (e.key === "ArrowLeft" || e.key === "PageUp") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!plan) return <div className="coloring-scroll"><p className="muted" style={{ textAlign: "center" }}>Loading preview…</p></div>;

  if (plan.pages.length === 0) {
    return (
      <div className="coloring-scroll">
        <div className="coloring-empty">
          <Icon name="book" size={30} style={{ color: "var(--text-faint)" }} />
          <h3>Nothing to preview yet</h3>
          <p className="muted">Generate or upload artwork for your pages and they'll appear here as a book you can flip through.</p>
          <button className="btn btn-primary" onClick={() => goTo("pages")}>Go to Pages</button>
        </div>
      </div>
    );
  }

  const [left, right] = views[view] ?? [null, null];
  const label = describeView(left, right);

  return (
    <div className="preview-wrap">
      <div className="preview-bar">
        <label className="pdf-toggle" style={{ margin: 0 }}>
          <input type="checkbox" checked={showBlanks} onChange={(e) => { setShowBlanks(e.target.checked); setView(0); }} />
          <span>Show blank pages</span>
        </label>
        <span className="faint" style={{ fontSize: 12 }}>{label}</span>
        <div className="spacer" />
        <span className="faint" style={{ fontSize: 12 }}>{view + 1} / {views.length}</span>
      </div>

      <div className="preview-stage" ref={stageRef}>
        <button className="preview-zone preview-zone-left" onClick={() => go(-1)} disabled={view <= 0} aria-label="Previous page" />
        <div
          className={`preview-book turn-${dir} ${wide ? "two" : "one"}`}
          key={`${view}-${wide}`}
          style={{ ["--ratio" as string]: book.trimWidth / book.trimHeight }}
        >
          {wide && <div className="preview-slot">{left && <SheetView sheet={left} plan={plan} />}</div>}
          <div className="preview-slot">{right && <SheetView sheet={right} plan={plan} />}</div>
          {wide && left && right && <div className="preview-spine" />}
        </div>
        <button className="preview-zone preview-zone-right" onClick={() => go(1)} disabled={view >= views.length - 1} aria-label="Next page" />
      </div>
      <p className="faint" style={{ fontSize: 11.5, textAlign: "center", margin: "8px 0 0" }}>
        Click the page edges or use the arrow keys. This follows your Print Settings, so it matches the exported PDF.
      </p>
    </div>
  );
}

function describeView(left: Sheet | null, right: Sheet | null): string {
  const names = [left, right].filter((s): s is Sheet => !!s && s.kind !== "paper");
  if (names.length === 1 && names[0]!.kind === "cover-front") return "Front cover";
  if (names.length === 1 && names[0]!.kind === "cover-back") return "Back cover";
  const nums = names.filter((s): s is Extract<Sheet, { kind: "leaf" }> => s.kind === "leaf").map((s) => s.leafNumber);
  if (nums.length === 0) return "Inside cover";
  return nums.length === 1 ? `Page ${nums[0]}` : `Pages ${nums[0]}–${nums[nums.length - 1]}`;
}

function SheetView({ sheet, plan }: { sheet: Sheet; plan: PreviewPlan }) {
  const { projectId, state } = useColoring();
  const { book, project } = state;
  const aspect = `${book.trimWidth} / ${book.trimHeight}`;
  const margin = `${Math.min(18, (book.print.marginIn / book.trimWidth) * 100)}%`;
  const author = book.cover.author || project.author || "";

  if (sheet.kind === "paper") return <div className="preview-page" style={{ aspectRatio: aspect }} />;
  if (sheet.kind === "cover-front") return <div className="preview-page cover"><CoverSheet side="front" variantId={plan.frontVariantId} /></div>;
  if (sheet.kind === "cover-back") return <div className="preview-page cover"><CoverSheet side="back" variantId={plan.backVariantId} /></div>;

  const { leaf, leafNumber } = sheet;
  let body: ReactNode = null;
  switch (leaf.kind) {
    case "art": {
      const pg = plan.pages.find((p) => p.id === leaf.pageId);
      body = pg ? (
        <>
          <img className="preview-art" style={{ padding: margin }} src={coloringApi.imageUrl(projectId, pg.variantId)} alt={pg.title} />
          {book.print.pageNumbers && <span className="preview-folio">{leafNumber}</span>}
        </>
      ) : null;
      break;
    }
    case "title":
      body = (
        <div className="preview-center">
          <div className="pv-title">{project.title}</div>
          {project.subtitle && <div className="pv-subtitle">{project.subtitle}</div>}
          {author && <div className="pv-author">{author}</div>}
        </div>
      );
      break;
    case "copyright":
      body = <div className="pv-copyright">{book.print.copyrightText.trim() || `Copyright © ${new Date().getFullYear()} ${author || project.title}. All rights reserved.`}</div>;
      break;
    case "belongs":
      body = (
        <div className="preview-center">
          <div className="pv-belongs">This Book Belongs To</div>
          <div className="pv-line" />
        </div>
      );
      break;
    case "test":
      body = (
        <div className="preview-center">
          <div className="pv-belongs" style={{ fontSize: "6.5cqw" }}>Color Test Page</div>
          <div className="pv-note">Try your markers, pencils and pens here before you start.</div>
          <svg viewBox="0 0 120 130" className="pv-shapes" aria-hidden="true">
            {[0, 1, 2, 3].flatMap((r) => [0, 1, 2].map((c) => {
              const k = (r * 3 + c) % 3, cx = 20 + c * 40, cy = 18 + r * 32;
              return k === 0 ? <circle key={`${r}${c}`} cx={cx} cy={cy} r={11} /> : k === 1 ? <rect key={`${r}${c}`} x={cx - 11} y={cy - 11} width={22} height={22} /> : <polygon key={`${r}${c}`} points={star(cx, cy, 13, 6)} />;
            }))}
          </svg>
        </div>
      );
      break;
    case "blank":
      body = <span className="pv-blank">blank</span>;
      break;
  }
  return <div className="preview-page" style={{ aspectRatio: aspect }}>{body}</div>;
}

function star(cx: number, cy: number, outer: number, inner: number): string {
  return Array.from({ length: 10 }, (_, i) => {
    const a = (Math.PI / 5) * i - Math.PI / 2;
    const r = i % 2 === 0 ? outer : inner;
    return `${(cx + Math.cos(a) * r).toFixed(1)},${(cy + Math.sin(a) * r).toFixed(1)}`;
  }).join(" ");
}
